/**
 * Edit Business Capability Route
 *
 * Form for editing existing business capabilities
 */

import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useCapability } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import { BusinessCapabilityForm } from '../../components/capabilities';
import {
  updateCapability,
  ApiError,
  type UpdateBusinessCapabilityInput,
} from '../../lib/apiClient';
import { LoadingSpinner, EmptyState } from '../../components/shared';
import type { CreateCapabilityFormData } from '../../components/forms/validation';
import '@tylertech/forge/icon';
import '@tylertech/forge/banner';
import './edit.css';

export const Route = createFileRoute('/capabilities/$capabilityId/edit')({
  component: EditCapabilityPage,
});

function EditCapabilityPage(): JSX.Element {
  const { capabilityId } = Route.useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, loading, error: fetchError } = useCapability(capabilityId);
  const capability = data?.capability;

  const IconElement = 'forge-icon' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  const handleSubmit = async (data: CreateCapabilityFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      const input: UpdateBusinessCapabilityInput = {
        name: data.name,
        description: data.description,
        level: data.level,
        parentCapabilityId: data.parentCapabilityId || undefined,
        criticality: data.criticality,
        strategicImportance: data.strategicImportance,
        lifecycleStatus: data.lifecycleStatus,
        investmentPriority: data.investmentPriority || undefined,
        businessOwnerId: data.businessOwnerId,
        owningBusinessUnitId: data.owningBusinessUnitId,
        stakeholderIds: data.stakeholderIds,
        maturityLevel: data.maturityLevel,
        targetMaturityLevel: data.targetMaturityLevel,
        currentInvestmentLevel: data.currentInvestmentLevel || undefined,
        targetInvestmentLevel: data.targetInvestmentLevel || undefined,
        planningHorizon: data.planningHorizon || undefined,
        notes: data.notes || undefined,
        assumptions: data.assumptions || undefined,
        tags: data.tags,
      };

      await updateCapability(capabilityId, input);
      await apolloClient.refetchQueries({
        include: ['GetCapability', 'ListCapabilities'],
      });
      navigate({
        to: '/capabilities/$capabilityId',
        params: { capabilityId },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: '/capabilities/$capabilityId', params: { capabilityId } });
  };

  if (loading) {
    return (
      <div className="edit-capability-page">
        <LoadingSpinner centered message="Loading capability..." />
      </div>
    );
  }

  if (fetchError || !capability) {
    return (
      <div className="edit-capability-page">
        <EmptyState
          icon="error"
          title="Failed to load capability"
          description={fetchError?.message || 'Capability not found'}
          actionLabel="Back to Business Capabilities"
          onAction={() => navigate({ to: '/capabilities', search: { page: 1, pageSize: 25 } })}
        />
      </div>
    );
  }

  const defaultValues: Partial<CreateCapabilityFormData> = {
    name: capability.name,
    description: capability.description || undefined,
    level: capability.level,
    parentCapabilityId: capability.parentCapabilityId || undefined,
    criticality: capability.criticality,
    strategicImportance: capability.strategicImportance,
    lifecycleStatus: capability.lifecycleStatus,
    investmentPriority: capability.investmentPriority || undefined,
    businessOwnerId: capability.businessOwnerId,
    owningBusinessUnitId: capability.owningBusinessUnitId,
    stakeholderIds: capability.stakeholderIds,
    maturityLevel: capability.maturityLevel || undefined,
    targetMaturityLevel: capability.targetMaturityLevel || undefined,
    currentInvestmentLevel: capability.currentInvestmentLevel || undefined,
    targetInvestmentLevel: capability.targetInvestmentLevel || undefined,
    planningHorizon: capability.planningHorizon || undefined,
    notes: capability.notes || undefined,
    assumptions: capability.assumptions || undefined,
    tags: capability.tags,
  };

  return (
    <div className="edit-capability-page">
      <div className="breadcrumb">
        <Link
          to="/capabilities/$capabilityId"
          params={{ capabilityId }}
          className="breadcrumb-link"
        >
          <IconElement name="arrow_back" aria-hidden="true" />
          <span>Back to {capability.name}</span>
        </Link>
      </div>

      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Edit Business Capability</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Update the details for {capability.name}
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <BusinessCapabilityForm
        mode="edit"
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}

export { EditCapabilityPage };
